import type { Fraction } from '../util/fractionType';

/**
 * Identifiers for the various sequences of segments that can be attached to an
 * item. In the case of browse preview, the sequences of segments only carry a
 * single stream each, while for edit proxy the sequence of segments contain
 * both the video stream and all the audio streams.
 */
export enum StreamRoleId {
  BrowsePreviewVideo = 'bpv',
  BrowsePreviewAudioDownmix = 'bpad',
  BrowsePreviewVideoAudioDownMix = 'bpvad',
  EditProxyFull = 'epf',
  LiveRecordHighresTs = 'lrht',
  LiveRecordWebproxyTs = 'lrwt',
  LiveRecordThumbnail = 'lrthb',
  PngPageProxy = 'ppp',
  LiveRecordAudioOnlyHighresTs = 'lrah',
  LiveRecordAudioOnlyProxyTs = 'lrap',
}

export type DirectS3SupportedStreamRoleId =
  StreamRoleId.LiveRecordWebproxyTs |
  StreamRoleId.LiveRecordHighresTs |
  StreamRoleId.LiveRecordThumbnail |
  StreamRoleId.LiveRecordAudioOnlyHighresTs |
  StreamRoleId.LiveRecordAudioOnlyProxyTs;

/**
 * The codec strings are MIME types with suitable for passing as an argument to
 * https://www.w3.org/TR/media-source/#dom-mediasource-addsourcebuffer which
 * describe how to interpret the byte sequence provided by a sequence of media
 * segment resources.
 *
 * The spec https://datatracker.ietf.org/doc/html/rfc6381 defines the
 * interpretation of the codecs parameter. E.g. avc1.42c020 is interpreted as
 *
 * {
 *   idIso: 'avc1', profileIdc: 0x42, constraintSetFlags: 0xc0, levelIdc: 0x20
 * }
 *
 * You can probe for some of this information using FFprobe, using the
 * '--show_streams' argument.
 *
 * $ ffprobe -show_streams recording_1.002000.ts 2>/dev/null | grep profile=
 * profile=LC --> Low Complexity --> H.264 Baseline --> 0x42
 *
 * levelIdc:
 *
 * $ ffprobe -show_streams recording_1.002000.ts 2>/dev/null | grep level=
 * level=32 --> 0x20
 *
 * We're not sure we are providing the exact correct values here, or if it
 * matters.
 *
 * More information:
 * https://developer.mozilla.org/en-US/docs/Web/Media/Formats/codecs_parameter
 */
export enum CodecString {
  MP4_VIDEO_H264_BASELINE_AVC_20 = 'video/mp4; codecs="avc1.42c020"',
  TS_VIDEO_H264_BASELINE_AVC_20 = 'video/mp2t; codecs="avc1.42c020"',
  MP4_AUDIO_AAC_LOW_COMPLEXITY = 'audio/mp4; codecs="mp4a.40.2"',
  MP4_VIDEO_WITH_AUDIO = 'video/mp4; codecs="avc1.42c020,mp4a.40.2"',
  MP4_VIDEO_WITH_FLAC = 'video/mp4; codecs="avc1.42c020,flac"',
  MPEGTS = 'video/mp2t; codecs="avc1.42c020,mp4a.40.2"',
  MPEGTS_AUDIO_ONLY = 'audio/mp2t; codecs="mp4a.40.2"',
  MP4_AUDIO_FLAC = 'audio/mp4; codecs="flac"',
}

export interface MediaAvailability {
  /**
   * The duration of media stored in each media segment.
   */
  segmentDuration: Fraction;

  /**
   * The available streams and their codec strings.
   *
   */
  codecsByStreamId: Partial<Record<StreamRoleId, CodecString>>

  /**
   * An estimate of number of bytes per segment.
   */
  sizeByStreamId?: Partial<Record<StreamRoleId, number>>;

  /**
   * A list of streams currently available or expected available in the future
   * for this item.
   */
  expectedStreams?: StreamRoleId[],

  /**
   * The number of media carrying media segments that have been ingested. Note
   * that for fragmented Mp4 this excludes the init segment. This means that
   * for both MpegTS / Fragmented Mp4 the total duration can be calculated
   * with the following expression:
   *
   *    mediaDuration = segmentCount * segmentDuration
   */
  segmentCount: number;

  /**
   *  the start timestamp of the first received media
   */
  streamStartTimestamp?: string;

  /**
   *  is media currently streaming
   */
  streaming: boolean;
}
